import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ProgressBar, Card, Badge, Accordion, Form, Button, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FiCheck, FiUser, FiAlertCircle, FiMail, FiSend, FiClock, FiEdit3, FiHome, FiMessageSquare, FiLock } from 'react-icons/fi';
import { Header, BottomNav, SkeletonList, ImageViewerModal } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../context/DateContext';
import { cleaningApi, CleaningItem, authApi, messageApi, memoApi, checkoutApi, DirectMessage, Memo, MemoComment } from '../api/client';
import { formatRoomAddress, formatRoomType, formatDbTime } from '../utils';

export const AdminDashboardPage = () => {
  const { user } = useAuth();
  const { selectedDate } = useDate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageInputs, setMessageInputs] = useState<Record<string, string>>({});
  const [memoInputs, setMemoInputs] = useState<Record<string, string>>({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showMemoHistoryModal, setShowMemoHistoryModal] = useState(false);
  const [cameFromRoomModal, setCameFromRoomModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [selectedRoom, setSelectedRoom] = useState<CleaningItem | null>(null);
  const [viewerImage, setViewerImage] = useState<string | null>(null);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Level 1, 2만 접근 가능
  if (user && user.level > 2) {
    return <Navigate to="/" replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ['cleaning-list', dateStr],
    queryFn: () => cleaningApi.getList(dateStr),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => authApi.getUsers(),
  });

  // 메시지 내역 조회
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['messages', dateStr],
    queryFn: () => messageApi.getMessages(dateStr),
  });

  // 객실별 전달사항 조회
  const { data: memosData } = useQuery({
    queryKey: ['memos', dateStr],
    queryFn: () => memoApi.getMemos(dateStr),
  });

  // 퇴근 상태 조회
  const { data: checkoutData } = useQuery({
    queryKey: ['checkout-all', dateStr],
    queryFn: () => checkoutApi.getAll(dateStr),
  });

  // 선택된 객실의 메모 히스토리
  const { data: memoHistoryData, isLoading: memoHistoryLoading } = useQuery({
    queryKey: ['memo-history', dateStr, selectedRoom?.roomId],
    queryFn: () => memoApi.getComments(dateStr, selectedRoom!.roomId),
    enabled: !!selectedRoom && showMemoHistoryModal,
  });

  // 메시지 전송 mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ receiverId, message }: { receiverId: string; message: string }) =>
      messageApi.sendMessage(receiverId, message, dateStr),
    onSuccess: (_, variables) => {
      toast.success('메시지가 전송되었습니다.');
      setMessageInputs(prev => ({ ...prev, [variables.receiverId]: '' }));
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: () => {
      toast.error('메시지 전송에 실패했습니다.');
    },
  });

  const handleSendMessage = (receiverId: string) => {
    const message = messageInputs[receiverId]?.trim();
    if (!message) {
      toast.error('메시지를 입력해주세요.');
      return;
    }
    sendMessageMutation.mutate({ receiverId, message });
  };

  const handleViewHistory = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setShowHistoryModal(true);
  };

  // 전달사항 저장 mutation
  const saveMemoMutation = useMutation({
    mutationFn: ({ roomId, memo }: { roomId: string; memo: string }) =>
      memoApi.saveMemo(dateStr, roomId, memo),
    onSuccess: () => {
      toast.success('전달사항이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['memos'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
    },
    onError: () => {
      toast.error('전달사항 저장에 실패했습니다.');
    },
  });

  const handleSaveMemo = (roomId: string) => {
    const memo = memoInputs[roomId] ?? '';
    saveMemoMutation.mutate({ roomId, memo });
  };

  const handleRoomClick = (item: CleaningItem) => {
    setSelectedRoom(item);
    // 기존 전달사항이 있으면 입력란에 설정
    const existingMemo = memos.find((m: Memo) => m.roomId === item.roomId);
    setMemoInputs(prev => ({ ...prev, [item.roomId]: existingMemo?.memo || '' }));
    setShowRoomModal(true);
  };

  const rawList = data?.data.data?.list || [];

  // 객실 정렬 (A동 → B동, 호수 오름차순)
  const list = [...rawList].sort((a: CleaningItem, b: CleaningItem) => {
    const addrA = (a.roomInfo?.room_address || a.roomId || '').toUpperCase();
    const addrB = (b.roomInfo?.room_address || b.roomId || '').toUpperCase();

    // 동(첫 글자)과 호수 분리
    const buildingA = addrA.charAt(0);
    const buildingB = addrB.charAt(0);
    const roomNumA = addrA.slice(1).padStart(4, '0');
    const roomNumB = addrB.slice(1).padStart(4, '0');

    // 동 먼저 비교, 같으면 호수 비교
    if (buildingA !== buildingB) {
      return buildingA.localeCompare(buildingB);
    }
    return roomNumA.localeCompare(roomNumB);
  });

  const memos = memosData?.data.data?.memos || [];
  const allMessages = messagesData?.data.data?.messages || [];
  const stats = data?.data.data?.stats;
  const users = usersData?.data.data?.users || [];
  const checkoutStatuses = (checkoutData?.data.data?.statuses || []) as Array<{
    userId: string;
    checkoutTime: string | null;
    user: { id: string; name: string };
  }>;

  // 사용자별 퇴근 시간 찾기
  const getUserCheckoutTime = (userId: string): string | null => {
    const status = checkoutStatuses.find(s => s.userId === userId);
    return status?.checkoutTime || null;
  };

  // 사용자별 통계 계산
  const userStats = users.map((u) => {
    const userRooms = list.filter((item: CleaningItem) => item.userId === u.id);
    const completed = userRooms.filter((item: CleaningItem) => item.completed && !item.canceled).length;
    const canceled = userRooms.filter((item: CleaningItem) => item.canceled).length;
    const total = userRooms.length;
    const active = total - canceled;
    const percent = active > 0 ? Math.round((completed / active) * 100) : 0;

    // 해당 사용자에게 보낸 메시지 통계
    const sentMessages = allMessages.filter((msg: DirectMessage) => msg.receiverId === u.id);
    const readMessages = sentMessages.filter((msg: DirectMessage) => msg.isRead);

    // 퇴근 시간
    const checkoutTime = getUserCheckoutTime(u.id);

    return {
      ...u,
      total,
      completed,
      canceled,
      active,
      percent,
      sentCount: sentMessages.length,
      readCount: readMessages.length,
      checkoutTime,
      rooms: userRooms, // 담당 객실 목록 추가
    };
  }).filter(u => u.total > 0).sort((a, b) => b.total - a.total);

  // 미배정 객실 목록
  const unassignedRooms = list.filter((item: CleaningItem) => !item.userId);

  // 선택된 사용자의 메시지 내역
  const selectedUserMessages = allMessages.filter(
    (msg: DirectMessage) => msg.receiverId === selectedUserId
  );

  const progressPercent = stats ? stats.percentage : 0;

  return (
    <div className="app-container">
      <Header
        showDatePicker
        showProfile
        userName={user?.name}
        onProfileClick={() => navigate('/settings')}
      />

      <main className="main-content">
        {/* 전체 진행률 */}
        <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold">전체 진행률</span>
            <span className="text-primary fw-bold fs-5">{progressPercent}%</span>
          </div>
          <ProgressBar
            now={progressPercent}
            variant={progressPercent === 100 ? 'success' : 'primary'}
            style={{ height: '12px' }}
          />
          <div className="d-flex justify-content-between mt-2">
            <span>
              완료 <strong>{stats?.completed || 0}</strong> / {(stats?.total || 0) - (stats?.canceled || 0)}
            </span>
            <span className="text-danger">
              취소 <strong>{stats?.canceled || 0}</strong>
            </span>
          </div>
        </div>

        {/* 사용자별 진행률 */}
        <h6 className="mb-3">담당자별 현황</h6>

        {isLoading ? (
          <SkeletonList count={4} />
        ) : userStats.length === 0 ? (
          <div className="text-center text-muted py-4">
            아직 객실을 선택한 담당자가 없습니다.
          </div>
        ) : (
          <div className="d-flex flex-column gap-2 mb-4">
            {userStats.map((u) => (
              <Card key={u.id} className="shadow-sm">
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-bold">
                      <FiUser className="me-1" />
                      {u.name}
                      {u.checkoutTime && (
                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '10px' }}>
                          <FiClock className="me-1" />
                          퇴근 {formatDbTime(u.checkoutTime, 'HH:mm')}
                        </Badge>
                      )}
                    </span>
                    <span className={u.percent === 100 ? 'text-success fw-bold' : ''}>
                      {u.percent}%
                    </span>
                  </div>
                  <ProgressBar
                    now={u.percent}
                    variant={u.percent === 100 ? 'success' : 'info'}
                    style={{ height: '6px' }}
                    className="mb-1"
                  />
                  <div className="d-flex justify-content-between small text-muted">
                    <span>완료 {u.completed} / {u.active}</span>
                    {u.canceled > 0 && <span className="text-danger">취소 {u.canceled}</span>}
                  </div>

                  {/* 배정 객실 목록 */}
                  <Accordion className="mt-2">
                    <Accordion.Item eventKey="rooms">
                      <Accordion.Header className="py-0">
                        <small>
                          <FiHome className="me-1" />
                          배정 객실 ({u.total})
                        </small>
                      </Accordion.Header>
                      <Accordion.Body className="p-2">
                        <div className="d-flex flex-column gap-2">
                          {u.rooms.map((room: CleaningItem) => {
                            const roomMemo = memos.find((m: Memo) => m.roomId === room.roomId);
                            const statusTime = room.completedTime ? formatDbTime(room.completedTime, 'HH:mm') : null;

                            let statusBadge;
                            if (room.canceled) {
                              statusBadge = (
                                <Badge bg="danger">
                                  {room.noshow ? '노쇼' : '취소'}
                                  {statusTime && <span className="ms-1">{statusTime}</span>}
                                </Badge>
                              );
                            } else if (room.completed) {
                              statusBadge = (
                                <Badge bg="success">
                                  <FiCheck /> 완료
                                  {statusTime && <span className="ms-1">{statusTime}</span>}
                                </Badge>
                              );
                            } else {
                              statusBadge = <Badge bg="primary">진행중</Badge>;
                            }

                            return (
                              <div
                                key={room.id}
                                className={`bg-light rounded p-2 ${room.canceled ? 'opacity-50' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRoomClick(room)}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold small">
                                      {formatRoomAddress(room.roomInfo?.room_address) || room.roomId}
                                    </span>
                                    {room.isJaesil && (
                                      <Badge bg="warning" text="dark">재실</Badge>
                                    )}
                                    {statusBadge}
                                    {roomMemo?.memo && (
                                      <FiEdit3 className="text-info" style={{ fontSize: '12px' }} />
                                    )}
                                    {(room.memoCount ?? 0) > 0 && (
                                      <Badge
                                        bg="info"
                                        pill
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedRoom(room);
                                          setShowMemoHistoryModal(true);
                                        }}
                                      >
                                        <FiMessageSquare className="me-1" />
                                        {room.memoCount}
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                {/* 객실 타입 + 비밀번호 */}
                                {(room.roomInfo?.room_type || room.password) && (
                                  <div className="mt-1 small text-muted">
                                    {room.roomInfo?.room_type && formatRoomType(room.roomInfo.room_type)}
                                    {room.roomInfo?.room_type && room.password && ' '}
                                    {room.password && (
                                      <span className="text-secondary">
                                        <FiLock className="me-1" />
                                        {room.password}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* 전달사항 */}
                                {roomMemo?.memo && (
                                  <div className="mt-1 small text-info">
                                    <strong>전달:</strong> {roomMemo.memo}
                                  </div>
                                )}

                                {/* 체크인/체크아웃 메모 */}
                                {(room.checkinMemo || room.checkoutMemo) && (
                                  <div className="mt-1 small">
                                    {room.checkoutMemo && (
                                      <div className="text-danger">
                                        <strong>퇴실:</strong> {room.checkoutMemo}
                                      </div>
                                    )}
                                    {room.checkinMemo && (
                                      <div className="text-primary">
                                        <strong>입실:</strong> {room.checkinMemo}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  {/* DM 전송 섹션 */}
                  <Accordion className="mt-2">
                    <Accordion.Item eventKey="dm">
                      <Accordion.Header className="py-0">
                        <small>
                          <FiMail className="me-1" />
                          메시지 (전송: {u.sentCount}, 확인: {u.readCount})
                        </small>
                      </Accordion.Header>
                      <Accordion.Body className="p-2">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="메시지를 입력하세요..."
                          value={messageInputs[u.id] || ''}
                          onChange={(e) => setMessageInputs(prev => ({ ...prev, [u.id]: e.target.value }))}
                          className="mb-2"
                          style={{ fontSize: '14px' }}
                        />
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleSendMessage(u.id)}
                            disabled={sendMessageMutation.isPending}
                          >
                            <FiSend className="me-1" />
                            보내기
                          </Button>
                          {u.sentCount > 0 && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleViewHistory(u.id, u.name)}
                            >
                              내역
                            </Button>
                          )}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Card.Body>
              </Card>
            ))}

            {/* 미배정 객실 섹션 */}
            {unassignedRooms.length > 0 && (
              <Card className="shadow-sm border-secondary">
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold text-secondary">
                      <FiAlertCircle className="me-1" />
                      미배정 객실
                    </span>
                    <Badge bg="secondary">{unassignedRooms.length}개</Badge>
                  </div>

                  {/* 미배정 객실 목록 */}
                  <Accordion>
                    <Accordion.Item eventKey="unassigned-rooms">
                      <Accordion.Header className="py-0">
                        <small>
                          <FiHome className="me-1" />
                          객실 목록 ({unassignedRooms.length})
                        </small>
                      </Accordion.Header>
                      <Accordion.Body className="p-2">
                        <div className="d-flex flex-column gap-2">
                          {unassignedRooms.map((room: CleaningItem) => {
                            const roomMemo = memos.find((m: Memo) => m.roomId === room.roomId);

                            return (
                              <div
                                key={room.id}
                                className="bg-light rounded p-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRoomClick(room)}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold small">
                                      {formatRoomAddress(room.roomInfo?.room_address) || room.roomId}
                                    </span>
                                    {room.isJaesil && (
                                      <Badge bg="warning" text="dark">재실</Badge>
                                    )}
                                    <Badge bg="secondary">대기</Badge>
                                    {roomMemo?.memo && (
                                      <FiEdit3 className="text-info" style={{ fontSize: '12px' }} />
                                    )}
                                    {(room.memoCount ?? 0) > 0 && (
                                      <Badge
                                        bg="info"
                                        pill
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedRoom(room);
                                          setShowMemoHistoryModal(true);
                                        }}
                                      >
                                        <FiMessageSquare className="me-1" />
                                        {room.memoCount}
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                {/* 객실 타입 + 비밀번호 */}
                                {(room.roomInfo?.room_type || room.password) && (
                                  <div className="mt-1 small text-muted">
                                    {room.roomInfo?.room_type && formatRoomType(room.roomInfo.room_type)}
                                    {room.roomInfo?.room_type && room.password && ' '}
                                    {room.password && (
                                      <span className="text-secondary">
                                        <FiLock className="me-1" />
                                        {room.password}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* 전달사항 */}
                                {roomMemo?.memo && (
                                  <div className="mt-1 small text-info">
                                    <strong>전달:</strong> {roomMemo.memo}
                                  </div>
                                )}

                                {/* 체크인/체크아웃 메모 */}
                                {(room.checkinMemo || room.checkoutMemo) && (
                                  <div className="mt-1 small">
                                    {room.checkoutMemo && (
                                      <div className="text-danger">
                                        <strong>퇴실:</strong> {room.checkoutMemo}
                                      </div>
                                    )}
                                    {room.checkinMemo && (
                                      <div className="text-primary">
                                        <strong>입실:</strong> {room.checkinMemo}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Card.Body>
              </Card>
            )}
          </div>
        )}

        {/* 전체 객실 목록 */}
        <h6 className="mb-3">전체 객실 현황</h6>

        {isLoading ? (
          <SkeletonList count={6} />
        ) : (
          <div className="d-flex flex-column gap-2">
            {list.map((item: CleaningItem) => {
              let statusBadge;
              const roomMemo = memos.find((m: Memo) => m.roomId === item.roomId);

              // 완료/취소 시간
              const statusTime = item.completedTime ? formatDbTime(item.completedTime, 'HH:mm') : null;

              if (item.canceled) {
                statusBadge = (
                  <Badge bg="danger">
                    {item.noshow ? '노쇼' : '취소'}
                    {statusTime && <span className="ms-1">{statusTime}</span>}
                  </Badge>
                );
              } else if (item.completed) {
                statusBadge = (
                  <Badge bg="success">
                    <FiCheck /> 완료
                    {statusTime && <span className="ms-1">{statusTime}</span>}
                  </Badge>
                );
              } else if (item.userId) {
                statusBadge = <Badge bg="primary">진행중</Badge>;
              } else {
                statusBadge = <Badge bg="secondary">대기</Badge>;
              }

              return (
                <div
                  key={item.id}
                  className={`bg-white rounded-3 p-3 shadow-sm ${item.canceled ? 'opacity-50' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRoomClick(item)}
                >
                  {/* 1줄: 객실번호 + 재실뱃지 + 상태뱃지 + 메모아이콘 + 담당자 */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold">
                        {formatRoomAddress(item.roomInfo?.room_address) || item.roomId}
                      </span>
                      {item.isJaesil && (
                        <Badge bg="warning" text="dark">재실</Badge>
                      )}
                      {statusBadge}
                      {roomMemo?.memo && (
                        <FiEdit3 className="text-info" style={{ fontSize: '14px' }} />
                      )}
                      {(item.memoCount ?? 0) > 0 && (
                        <Badge
                          bg="info"
                          pill
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRoom(item);
                            setShowMemoHistoryModal(true);
                          }}
                        >
                          <FiMessageSquare className="me-1" />
                          {item.memoCount}
                        </Badge>
                      )}
                    </div>
                    <div className="small text-muted">
                      {item.userId ? (
                        <span>
                          <FiUser className="me-1" />
                          {item.userName || item.userId}
                        </span>
                      ) : (
                        <span>
                          <FiAlertCircle className="me-1" />
                          미배정
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 객실 타입 + 비밀번호 표시 */}
                  {(item.roomInfo?.room_type || item.password) && (
                    <div className="mt-2 small text-muted">
                      {item.roomInfo?.room_type && formatRoomType(item.roomInfo.room_type)}
                      {item.roomInfo?.room_type && item.password && ' '}
                      {item.password && (
                        <span className="text-secondary">
                          <FiLock className="me-1" />
                          {item.password}
                        </span>
                      )}
                    </div>
                  )}

                  {/* 전달사항 표시 */}
                  {roomMemo?.memo && (
                    <div className="mt-2 small text-info">
                      <FiEdit3 className="me-1" />
                      <strong>전달:</strong> {roomMemo.memo}
                    </div>
                  )}

                  {/* 체크인/체크아웃 메모 표시 */}
                  {(item.checkinMemo || item.checkoutMemo) && (
                    <div className="mt-2 small">
                      {item.checkoutMemo && (
                        <div className="text-danger">
                          <FiAlertCircle className="me-1" />
                          <strong>퇴실:</strong> {item.checkoutMemo}
                        </div>
                      )}
                      {item.checkinMemo && (
                        <div className="text-primary">
                          <FiAlertCircle className="me-1" />
                          <strong>입실:</strong> {item.checkinMemo}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && list.length === 0 && (
          <div className="text-center text-muted py-5">
            이 날짜에 청소 목록이 없습니다.
          </div>
        )}
      </main>

      <BottomNav />

      {/* 메시지 내역 모달 */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUserName}님 메시지 내역</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {messagesLoading ? (
            <div className="text-center py-4">로딩 중...</div>
          ) : selectedUserMessages.length === 0 ? (
            <div className="text-center text-muted py-4">보낸 메시지가 없습니다.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {selectedUserMessages.map((msg: DirectMessage) => (
                <div
                  key={msg.id}
                  className={`p-2 rounded ${msg.isRead ? 'bg-light' : 'bg-warning bg-opacity-10 border border-warning'}`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">
                      <FiClock className="me-1" />
                      {formatDbTime(msg.createdAt, 'HH:mm')}
                    </small>
                    {msg.isRead ? (
                      <Badge bg="success" style={{ fontSize: '10px' }}>
                        <FiCheck className="me-1" />
                        확인됨 {msg.readAt && formatDbTime(msg.readAt, 'HH:mm')}
                      </Badge>
                    ) : (
                      <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>
                        미확인
                      </Badge>
                    )}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 객실 상세/전달사항 모달 */}
      <Modal show={showRoomModal} onHide={() => setShowRoomModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {formatRoomAddress(selectedRoom?.roomInfo?.room_address) || selectedRoom?.roomId}
            {selectedRoom?.isJaesil && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '12px' }}>
                재실
              </Badge>
            )}
            {(selectedRoom?.memoCount ?? 0) > 0 && (
              <Badge
                bg="info"
                pill
                className="ms-2"
                style={{ fontSize: '12px', cursor: 'pointer' }}
                onClick={() => {
                  setShowRoomModal(false);
                  setCameFromRoomModal(true);
                  setShowMemoHistoryModal(true);
                }}
              >
                <FiMessageSquare className="me-1" />
                {selectedRoom?.memoCount}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* 객실 정보 */}
          <div className="mb-3 p-2 bg-light rounded small">
            <div className="d-flex justify-content-between">
              <span className="text-muted">타입</span>
              <span>{formatRoomType(selectedRoom?.roomInfo?.room_type)}</span>
            </div>
            <div className="d-flex justify-content-between mt-1">
              <span className="text-muted">담당자</span>
              <span>{selectedRoom?.userName || '미배정'}</span>
            </div>
            {selectedRoom?.password && (
              <div className="d-flex justify-content-between mt-1">
                <span className="text-muted">비밀번호</span>
                <span>
                  <FiLock className="me-1" />
                  {selectedRoom.password}
                </span>
              </div>
            )}
            <div className="d-flex justify-content-between mt-1">
              <span className="text-muted">상태</span>
              <span>
                {selectedRoom?.canceled ? (
                  <Badge bg="danger">
                    {selectedRoom.noshow ? '노쇼' : '취소'}
                  </Badge>
                ) : selectedRoom?.completed ? (
                  <Badge bg="success">
                    <FiCheck className="me-1" />완료
                  </Badge>
                ) : selectedRoom?.userId ? (
                  <Badge bg="primary">진행중</Badge>
                ) : (
                  <Badge bg="secondary">대기</Badge>
                )}
              </span>
            </div>
          </div>

          {/* 전달사항 입력 */}
          <Form.Group>
            <Form.Label className="fw-bold">
              <FiEdit3 className="me-1" />
              전달사항
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="담당자에게 전달할 사항을 입력하세요..."
              value={selectedRoom ? (memoInputs[selectedRoom.roomId] ?? '') : ''}
              onChange={(e) => selectedRoom && setMemoInputs(prev => ({ ...prev, [selectedRoom.roomId]: e.target.value }))}
            />
            <Form.Text className="text-muted">
              입력한 전달사항은 담당자의 내청소 화면에 표시됩니다.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoomModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (selectedRoom) {
                handleSaveMemo(selectedRoom.roomId);
                setShowRoomModal(false);
              }
            }}
            disabled={saveMemoMutation.isPending}
          >
            {saveMemoMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 메모 히스토리 모달 */}
      <Modal show={showMemoHistoryModal} onHide={() => {
        setShowMemoHistoryModal(false);
        setCameFromRoomModal(false);
      }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {formatRoomAddress(selectedRoom?.roomInfo?.room_address) || selectedRoom?.roomId} 메모
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {memoHistoryLoading ? (
            <div className="text-center py-4">로딩 중...</div>
          ) : (memoHistoryData?.data.data?.comments || []).length === 0 ? (
            <div className="text-center text-muted py-4">메모가 없습니다.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {(memoHistoryData?.data.data?.comments || []).map((memo: MemoComment) => (
                <div key={memo.id} className="border rounded p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="fw-bold">{memo.user.name}</span>
                    <small className="text-muted">{formatDbTime(memo.createdAt)}</small>
                  </div>
                  <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{memo.comment}</p>
                  {memo.images && memo.images.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap">
                      {memo.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.thumbnailUrl || img.imageUrl}
                          alt="Memo"
                          className="rounded"
                          style={{ maxHeight: '100px', cursor: 'pointer' }}
                          onClick={() => setViewerImage(img.imageUrl)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {cameFromRoomModal ? (
            <Button variant="outline-secondary" onClick={() => {
              setShowMemoHistoryModal(false);
              setCameFromRoomModal(false);
              setShowRoomModal(true);
            }}>
              ← 뒤로
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setShowMemoHistoryModal(false)}>
              닫기
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* 이미지 뷰어 모달 */}
      <ImageViewerModal
        show={!!viewerImage}
        imageUrl={viewerImage}
        onHide={() => setViewerImage(null)}
      />
    </div>
  );
};
